import React, { useEffect, useState } from 'react';
import { api } from '../../services/api';

interface Props {
  strategyId?: string;
  onComplete?: () => void;
}

const FinalSummary: React.FC<Props> = ({ strategyId, onComplete }) => {
  const [strategyData, setStrategyData] = useState<any>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadStrategy = async () => {
      if (!strategyId) return;
      try {
        const res = await api.get(`/strategies/${strategyId}`);
        setStrategyData(res.data);
      } catch (err) {
        console.error('Error al cargar la estrategia:', err);
      } finally {
        setLoading(false);
      }
    };
    loadStrategy();
  }, [strategyId]);

  const handleExport = async () => {
    try {
      const res = await api.get(`/strategies/${strategyId}/export`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Estrategia_JJAgency.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando PDF:', err);
      alert('Error al exportar el PDF. Asegúrate de estar autenticado.');
    }
  };

  // Extraer datos de Módulo Z y Módulo 6-B si existen
  let diagnosticZ: any = null;
  if (strategyData?.module_z_diagnostic) {
    try {
      diagnosticZ = typeof strategyData.module_z_diagnostic === 'string'
        ? JSON.parse(strategyData.module_z_diagnostic)
        : strategyData.module_z_diagnostic;
    } catch (e) {}
  }

  let actionables6B: any = null;
  if (strategyData?.module_6b_actionables) {
    try {
      actionables6B = typeof strategyData.module_6b_actionables === 'string'
        ? JSON.parse(strategyData.module_6b_actionables)
        : strategyData.module_6b_actionables;
    } catch (e) {}
  }

  return (
    <div style={styles.container}>
      <h2 style={styles.title}>¡Estrategia Finalizada con Éxito!</h2>
      <p style={styles.description}>
        Has consolidado la dirección estratégica alineada entre creatividad y medios.
      </p>

      {/* Tarjeta 1: Posición Competitiva (Módulo Z) */}
      {diagnosticZ && (
        <div style={styles.summaryCard}>
          <h3 style={styles.cardHeader}>📊 Posición Competitiva de Inversión (Módulo Z)</h3>
          <div style={styles.infoRow}>
            <strong>Escenario:</strong>
            <span style={styles.badge}>{diagnosticZ.clasificacion || 'No especificado'}</span>
          </div>
          {diagnosticZ.principio_estrategico && (
            <div style={styles.infoRow}>
              <strong>Principio Estratégico:</strong>
              <span>{diagnosticZ.principio_estrategico}</span>
            </div>
          )}
        </div>
      )}

      {/* Tarjeta 2: Arquitectura Integrada (Módulo 6-B) */}
      {actionables6B && actionables6B.tareas && (
        <div style={styles.summaryCard}>
          <h3 style={styles.cardHeader}>🚀 Arquitectura Integrada de Accionables (Módulo 6-B)</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            {actionables6B.tareas.map((t: any, idx: number) => (
              <div key={idx} style={styles.taskItem}>
                <strong>Accionable {idx + 1}: {t.titulo}</strong>
                <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: 'var(--text-secondary)' }}>{t.direccion_integrada}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div style={styles.card}>
        <h3 style={{ fontSize: '18px', color: 'var(--text-primary)', marginBottom: '16px' }}>Cierre Definitivo del Flujo</h3>
        <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
          <button style={styles.primaryButton} onClick={handleExport}>
            📥 Exportar Estrategia a PDF
          </button>
          <button style={styles.secondaryButton} onClick={() => window.location.href = '/'}>
            🏠 Ir al Dashboard
          </button>
        </div>
      </div>
    </div>
  );
};

const styles: { [key: string]: React.CSSProperties } = {
  container: { display: 'flex', flexDirection: 'column', gap: '20px', textAlign: 'center', alignItems: 'center', marginTop: '16px' },
  title: { fontSize: '26px', color: 'var(--accent)', marginBottom: '4px', fontWeight: 700 },
  description: { color: 'var(--text-secondary)', marginBottom: '12px', maxWidth: '600px', lineHeight: 1.5, fontSize: '15px' },
  summaryCard: {
    padding: '24px',
    border: '1px solid var(--border)',
    borderRadius: '8px',
    background: 'var(--bg-card)',
    width: '100%',
    maxWidth: '650px',
    textAlign: 'left',
    boxShadow: 'var(--shadow)',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px'
  },
  cardHeader: { fontSize: '16px', fontWeight: 600, color: 'var(--text-primary)', margin: 0 },
  infoRow: { display: 'flex', gap: '8px', alignItems: 'center', fontSize: '14px', color: 'var(--text-primary)' },
  badge: { background: 'var(--accent)', color: '#fff', padding: '4px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: 600 },
  taskItem: { background: 'var(--bg-hover)', border: '1px solid var(--border)', padding: '12px', borderRadius: '6px', fontSize: '14px', color: 'var(--text-primary)' },
  primaryButton: { background: 'var(--accent)', color: '#fff', padding: '12px 24px', border: 'none', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' },
  secondaryButton: { background: 'transparent', border: '1px solid var(--border)', color: 'var(--text-primary)', padding: '12px 24px', borderRadius: '4px', fontWeight: 600, cursor: 'pointer' },
  card: { padding: '24px', border: '1px solid var(--border)', borderRadius: '8px', background: 'var(--bg-card)', width: '100%', maxWidth: '650px' }
};

export default FinalSummary;
